/**
 * <installation-guide> — sections/wallpaper-installation-guide.liquid
 * Marks the chapter being read in the contents lists (aria-current), and closes the mobile
 * contents panel once a link in it is used. The lists are plain anchor links, so they work
 * without this script.
 */
class InstallationGuide extends HTMLElement {
  connectedCallback() {
    this.links = [...this.querySelectorAll('.guide-toc__link[href^="#"]')];
    if (this.links.length === 0) return;

    const mobileToc = this.querySelector('.guide-toc--mobile');
    mobileToc?.addEventListener('click', (event) => {
      if (event.target.closest('.guide-toc__link')) mobileToc.open = false;
    });

    // Chapters can sit in a second copy of the section, so look across the whole page.
    const ids = new Set(this.links.map((link) => decodeURIComponent(link.hash.slice(1))));
    const chapters = [...document.querySelectorAll('[data-guide-chapter]')].filter((el) => ids.has(el.id));
    if (chapters.length === 0 || !('IntersectionObserver' in window)) return;

    this.visible = new Set();
    this.observer = new IntersectionObserver((entries) => this.onIntersect(entries, chapters), {
      rootMargin: '-20% 0px -60% 0px',
    });
    chapters.forEach((chapter) => this.observer.observe(chapter));
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  onIntersect(entries, chapters) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) this.visible.add(entry.target);
      else this.visible.delete(entry.target);
    });

    const current = chapters.find((chapter) => this.visible.has(chapter));
    if (!current) return;

    this.links.forEach((link) => {
      if (decodeURIComponent(link.hash.slice(1)) === current.id) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    });
  }
}

if (!customElements.get('installation-guide')) {
  customElements.define('installation-guide', InstallationGuide);
}
